import { clear, h } from '../core/dom'
import { haptic } from '../core/haptics'
import { go } from '../core/router'
import { exportAll, getSettings, updateSettings, wipe, type Reason } from '../core/store'
import { applyWarmShift } from '../core/theme'
import { canPrompt, notificationState, promptInstall, requestNightReminder } from '../core/install'
import { setLang, t, type Lang } from '../i18n'
import { clearCompanionHistory } from './companion'
import { sheet, toggle, topbar } from './shared'

export function renderSettings(root: HTMLElement): void {
  const paint = (): void => {
    clear(root)
    const d = t()
    const s = getSettings()

    const row = (name: string, desc: string | undefined, control: Node): HTMLElement =>
      h(
        'div',
        { class: 'setting' },
        h(
          'div',
          { class: 'grow' },
          h('div', { class: 'name' }, name),
          desc && h('div', { class: 'desc' }, desc),
        ),
        h('div', { class: 'control' }, control),
      )

    const langSeg = h(
      'div',
      { class: 'seg' },
      ...(['en', 'fa'] as Lang[]).map((code) =>
        h(
          'button',
          {
            class: s.lang === code ? 'on' : '',
            onclick: () => {
              haptic.tap()
              setLang(code)
              updateSettings({ lang: code })
              paint()
            },
          },
          code === 'fa' ? 'فا' : 'EN',
        ),
      ),
    )

    const notifState = notificationState()

    root.append(
      topbar(d.settings.title),
      h(
        'div',
        { class: 'stack-lg fade-in' },

        h(
          'div',
          { class: 'card' },
          row(d.settings.language, undefined, langSeg),
          row(
            d.settings.ritualTime,
            undefined,
            h('input', {
              type: 'time',
              value: s.ritualTime,
              style: { width: '128px', minHeight: '42px' },
              onchange: (e: Event) =>
                updateSettings({ ritualTime: (e.target as HTMLInputElement).value || '22:00' }),
            }),
          ),
          row(
            d.settings.notifications,
            notifState === 'denied' ? d.settings.blocked : d.settings.notificationsSub,
            notifState === 'granted'
              ? h('span', { class: 'dim' }, d.settings.enabled)
              : h(
                  'button',
                  {
                    class: 'btn sm',
                    disabled: notifState === 'denied' || notifState === 'unsupported',
                    onclick: async () => {
                      haptic.tap()
                      await requestNightReminder()
                      paint()
                    },
                  },
                  d.settings.enable,
                ),
          ),
        ),

        h(
          'div',
          { class: 'card' },
          row(
            d.settings.redShift,
            d.settings.redShiftSub,
            toggle(s.redShift, (v) => {
              updateSettings({ redShift: v })
              applyWarmShift()
            }),
          ),
          row(
            d.settings.voice,
            undefined,
            toggle(s.voiceGuidance, (v) => updateSettings({ voiceGuidance: v })),
          ),
          row(
            d.settings.haptics,
            undefined,
            toggle(s.haptics, (v) => updateSettings({ haptics: v })),
          ),
        ),

        h(
          'div',
          { class: 'card' },
          h(
            'div',
            { class: 'stack-sm', style: { paddingBottom: '12px' } },
            h('div', { class: 'name' }, d.settings.reason),
            h(
              'div',
              { class: 'chips' },
              ...(
                [
                  ['insomnia', d.onboarding.reasonInsomnia],
                  ['tapering', d.onboarding.reasonTapering],
                  ['forced', d.onboarding.reasonForced],
                  ['curious', d.onboarding.reasonCurious],
                ] as Array<[Reason, string]>
              ).map(([id, label]) =>
                h(
                  'button',
                  {
                    class: `chip ${s.reason === id ? 'on' : ''}`,
                    onclick: () => {
                      haptic.tap()
                      updateSettings({ reason: id })
                      paint()
                    },
                  },
                  label,
                ),
              ),
            ),
          ),
          h('div', { class: 'divider' }),
          h(
            'div',
            { class: 'stack-sm', style: { paddingTop: '12px' } },
            h('div', { class: 'name' }, d.settings.aiEndpoint),
            h('div', { class: 'desc' }, d.settings.aiEndpointSub),
            h('input', {
              type: 'url',
              value: s.aiEndpoint,
              placeholder: 'https://…/companion',
              onchange: (e: Event) =>
                updateSettings({ aiEndpoint: (e.target as HTMLInputElement).value.trim() }),
            }),
          ),
        ),

        canPrompt() &&
          h(
            'button',
            {
              class: 'btn block',
              onclick: () => {
                haptic.tap()
                void promptInstall()
              },
            },
            d.settings.install,
          ),

        h(
          'div',
          { class: 'card stack-sm' },
          h('div', { class: 'name' }, d.settings.data),
          h('div', { class: 'desc' }, d.settings.dataSub),
          h(
            'button',
            {
              class: 'btn ghost block',
              style: { marginTop: '8px' },
              onclick: () => {
                haptic.tap()
                void navigator.clipboard.writeText(exportAll()).catch(() => {
                  sheet(() =>
                    h(
                      'div',
                      { class: 'stack' },
                      h('h2', {}, d.settings.exportAll),
                      h('textarea', { rows: 14, value: exportAll(), readOnly: true }),
                    ),
                  )
                })
              },
            },
            d.settings.exportAll,
          ),
          h(
            'button',
            {
              class: 'btn danger block',
              onclick: () => {
                haptic.warn()
                confirmWipe(() => {
                  clearCompanionHistory()
                  go('onboarding')
                })
              },
            },
            d.settings.wipe,
          ),
        ),

        h(
          'button',
          {
            class: 'btn ghost block',
            onclick: () => {
              haptic.tap()
              go('safety')
            },
          },
          d.settings.safety,
        ),

        h('p', { class: 'faint center' }, `${d.app.name} · ${d.app.tagline}`),
      ),
    )
  }

  paint()
}

function confirmWipe(after: () => void): void {
  sheet((close) => {
    const d = t()
    return h(
      'div',
      { class: 'stack' },
      h('h2', {}, d.settings.wipe),
      h('p', {}, d.settings.wipeConfirm),
      h(
        'div',
        { class: 'row' },
        h('button', { class: 'btn ghost grow', onclick: close }, d.common.cancel),
        h(
          'button',
          {
            class: 'btn danger grow',
            onclick: () => {
              wipe()
              close()
              after()
            },
          },
          d.settings.wipe,
        ),
      ),
    )
  })
}
